import { ExternalLink, Phone, Mail, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import type { Brokerage } from '@/contracts/brokerage';
import { formatCurrency, formatSplit } from '@/contracts/brokerage';

interface BrokerageSidebarProps {
  brokerage: Brokerage;
  onRequestInfo: () => void;
}

export function BrokerageSidebar({ brokerage, onRequestInfo }: BrokerageSidebarProps) {
  return (
    <Card className="sticky top-24">
      <CardContent className="p-6 space-y-4">
        {/* Quick Stats */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Quick Facts</h3>
          
          <div className="space-y-2 text-sm">
            {(brokerage.split_starting_percent || brokerage.split_max_percent) && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Commission Split</span>
                <span className="font-semibold text-primary">
                  {formatSplit(brokerage.split_starting_percent, brokerage.split_max_percent)}
                </span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Cap</span>
              <span className="font-medium">
                {brokerage.cap_amount ? formatCurrency(brokerage.cap_amount) : 'No Cap'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Fees</span>
              <span className="font-medium">
                {brokerage.monthly_fee ? formatCurrency(brokerage.monthly_fee) : '$0'}
              </span>
            </div>
            
            {brokerage.transaction_fee && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Per Transaction</span>
                <span className="font-medium">
                  {brokerage.transaction_fee_type === 'percent' 
                    ? `${brokerage.transaction_fee}%`
                    : formatCurrency(brokerage.transaction_fee)
                  }
                </span>
              </div>
            )}
          </div>

          {/* Bonuses */}
          {(brokerage.sign_on_bonus_max || brokerage.has_revenue_share || brokerage.has_stock_awards) && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {brokerage.sign_on_bonus_max && (
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  Up to {formatCurrency(brokerage.sign_on_bonus_max)} Bonus
                </Badge>
              )}
              {brokerage.has_revenue_share && (
                <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                  Revenue Share
                </Badge>
              )}
              {brokerage.has_stock_awards && (
                <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                  Stock Awards
                </Badge>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* CTA */}
        <div className="space-y-3">
          <Button className="w-full" size="lg" onClick={onRequestInfo}>
            <MessageCircle className="h-4 w-4 mr-2" />
            Request Information
          </Button>
          
          {brokerage.website_url && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.open(brokerage.website_url!, '_blank')}
            >
              Visit Website
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Ideal Agent */}
        {brokerage.ideal_agent_description && (
          <>
            <Separator />
            <div>
              <p className="text-sm font-medium mb-2">Their Ideal Agent</p>
              <p className="text-sm text-muted-foreground">{brokerage.ideal_agent_description}</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
